import { SetModelJsonResource } from "@/types/setTypes";
import { displayWeight, formatFloat } from "@/lib/utils";
import { displayClaimedHourMinOnly } from "@/lib/dateUtils";
import { UserModelJsonResource } from "@/types/userTypes";
import { EditIcon, CopyIcon, MoreVerticalIcon, XIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { WorkoutModelJsonResource } from "@/types/workoutTypes";
import { ExerciseModelJsonResource } from "@/types/exerciseTypes";
import { Button } from "../ui/button";
import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { setApi } from "@/api/setApi";
import { useToastHandler } from "@/hooks/useToastHandler";
import { useInvalidateResources } from "@/hooks/useInvalidateResources";
import { motion, AnimatePresence } from "framer-motion";

interface OneExerciseCardProps {
  exercise: ExerciseModelJsonResource;
  workout: WorkoutModelJsonResource;
  sets: SetModelJsonResource[];
  user: UserModelJsonResource | null;
}

export function OneExerciseCard({
  exercise,
  workout,
  sets,
  user,
}: OneExerciseCardProps) {
  // State to track which set's delete button is revealed
  const [revealedDeleteSetId, setRevealedDeleteSetId] = useState<string | null>(
    null
  );

  // State for duplicate loading
  const [duplicatingSetId, setDuplicatingSetId] = useState<string | null>(null);

  // State for showing buttons
  const [showButtonsForSetId, setShowButtonsForSetId] = useState<string | null>(
    null
  );

  // State to track newly duplicated set for auto-scroll
  const [newlyDuplicatedSetId, setNewlyDuplicatedSetId] = useState<
    string | null
  >(null);

  const setRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const { handleSuccess, handleError } = useToastHandler();
  const invalidate = useInvalidateResources();
  const navigate = useNavigate();

  // Auto-scroll to newly duplicated set when it appears in the DOM
  useEffect(() => {
    if (newlyDuplicatedSetId) {
      const newSetElement = setRefs.current[newlyDuplicatedSetId];
      if (newSetElement) {
        // Element is now in the DOM, scroll to it
        setTimeout(() => {
          newSetElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 100); // Small delay for smooth experience
        // Clear the state after scrolling
        setNewlyDuplicatedSetId(null);
      }
    }
  }, [sets, newlyDuplicatedSetId]); // Trigger when sets data changes

  // Effect to handle clicks outside the revealed set
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if a delete button is revealed
      if (revealedDeleteSetId) {
        // Get the ref for the currently revealed set's container
        const currentSetRef = setRefs.current[revealedDeleteSetId];
        // If the ref exists and the click target is NOT inside the container
        if (currentSetRef && !currentSetRef.contains(event.target as Node)) {
          setRevealedDeleteSetId(null); // Hide the delete button
        }
      }
    };

    // Add listener only when a delete button is revealed
    if (revealedDeleteSetId) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      // Remove listener if no button is revealed (or was just hidden)
      document.removeEventListener("mousedown", handleClickOutside);
    }

    // Cleanup function to remove listener on unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [revealedDeleteSetId]);

  const duplicateMutation = useMutation({
    mutationFn: (setNanoId: string) => setApi.duplicateSet(setNanoId),
    onSuccess: (newSet) => {
      handleSuccess("Set duplicated successfully.");

      // Store the new set ID for scrolling after re-render
      setNewlyDuplicatedSetId(newSet.nano_id);

      invalidate([
        "/api/sets/recent",
        "/api/exercises/tracked",
        "/api/workouts",
        "/api/stats/overview",
        "/api/auth/me",
      ]);

      setDuplicatingSetId(null);
    },
    onError: () => {
      handleError("Failed to duplicate set.");
      setDuplicatingSetId(null);
    },
  });

  const handleEditClick = (set: SetModelJsonResource) => {
    navigate(`/workouts/set/${set.nano_id}`, {
      state: { set, workout: workout },
    });
  };

  const handleDuplicateClick = (setNanoId: string) => {
    setDuplicatingSetId(setNanoId);
    duplicateMutation.mutate(setNanoId);
  };

  // 计算单个训练的volume总和
  const exerciseVolumePercent = formatFloat(
    ((exercise._total_volume_lb ?? 0) / (workout.volume_lb ?? 1)) * 100,
    1
  );
  return (
    <div className="relative w-full">
      {/* 竖线 */}
      <div
        aria-hidden="true"
        className="absolute left-5 top-6 -ml-px mt-0.5 h-full w-[1px] bg-primary/30"
      />

      <div aria-current="step" className="group relative flex items-start ">
        {/* 圆点 */}
        <span aria-hidden="true" className="flex h-9 items-center mt-1">
          <span className="relative z-12 flex size-9 items-center justify-center rounded-full border-2 border-primary bg-white">
            <span className="text-[10px]">{exerciseVolumePercent}% </span>
          </span>
        </span>

        {/* 单项训练（含多个 set） */}
        <div className="ml-4 flex-1 min-w-0 flex-col">
          <div key={exercise.nano_id} className="flex flex-col gap-1 py-2">
            <div className="flex justify-between items-center  mb-2">
              {/* 单项训练名称 */}
              <Link to={`/stats/exercise-overview/${exercise.nano_id}`}>
                <h4 className="font-medium text-base text-primary hover:underline">
                  {exercise.name}
                </h4>
              </Link>
              {/* 单项训练的volume总和 */}
              <h4 className="text-xs rounded-sm text-tx-default bg-accent-bg-cool/10 px-2 py-0.5 w-[80px] text-center">
                {displayWeight(
                  exercise._total_volume_lb ?? 0,
                  exercise._total_volume_kg ?? 0,
                  user?.preferred_weight_unit
                )}
              </h4>
            </div>

            {/* set 列表 */}
            {sets
              .filter((set) => exercise._set_nano_ids?.includes(set.nano_id))
              .map((set) => (
                <div
                  key={set.nano_id}
                  // Assign ref to the container div using a statement block
                  ref={(el) => {
                    setRefs.current[set.nano_id] = el;
                  }}
                  className={
                    "flex flex-col rounded-md border transition-colors " +
                    (set.is_latest
                      ? " border-accent-bg-cold bg-accent-bg-cold/10"
                      : " border-bd-overlay bg-white ")
                  }
                >
                  {/* Set details (weight/time/reps) - Clickable */}
                  <div
                    className="flex items-center justify-between px-2 py-1.5 cursor-pointer hover:bg-accent/30 transition-colors"
                    onClick={() =>
                      setShowButtonsForSetId(
                        showButtonsForSetId === set.nano_id ? null : set.nano_id
                      )
                    }
                  >
                    <div className="flex-1 items-center">
                      <div className="flex items-center justify-start">
                        <span className="text-tx-default/70 rounded-md bg-bg-base px-2 py-1 text-sm">
                          {displayClaimedHourMinOnly(set.claimed_train_at)}
                        </span>
                        <span className="inline-flex px-2 py-1 font-medium">
                          {displayWeight(
                            set.weight_lb,
                            set.weight_kg,
                            user?.preferred_weight_unit
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Reps Display and More Icon */}
                    <div className="flex items-center gap-2">
                      <XIcon className="w-3 h-3 mx-2" />
                      <span className="text-tx-dark font-medium">
                        {set.reps}
                      </span>

                      {duplicatingSetId === set.nano_id && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                      )}
                      {duplicatingSetId !== set.nano_id && (
                        <MoreVerticalIcon className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>

                  {/* Horizontal Buttons - Only show when clicked with slide-down animation */}
                  <AnimatePresence>
                    {showButtonsForSetId === set.nano_id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="flex justify-center gap-2 px-2 pb-2 border-t border-bd-overlay/50 pt-2">
                          <Button
                            variant="outline"
                            size="default"
                            onClick={() => {
                              handleEditClick(set);
                              setShowButtonsForSetId(null);
                            }}
                            className="flex-1"
                          >
                            <EditIcon className="mr-2 h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            variant="primary"
                            size="default"
                            onClick={() => {
                              handleDuplicateClick(set.nano_id);
                              setShowButtonsForSetId(null);
                            }}
                            disabled={duplicatingSetId === set.nano_id}
                            className="flex-1"
                          >
                            <CopyIcon className="mr-2 h-4 w-4" />
                            Duplicate
                          </Button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
